// src/services/analytics.js — Google Analytics, behind consent, with the guest
// token surgically removed from everything that leaves this browser.
//
// ── WHAT THIS MEASURES ──────────────────────────────────────────────────────
// Which pages of the web app get opened, and — through GA's own IP-derived
// geography — roughly where from. It does NOT send a user id, an email, a chat
// id, a message, or anything typed. GA is told about routes and nothing else.
//
// 🟥 A GUEST LINK IS A SECRET, AND ITS SECRET IS IN THE URL. `/g/<token>` is the
//    whole authentication for a temporary chat: anyone holding that string can
//    open the conversation. GA4's default behaviour is to record `page_location`
//    — the full URL — on every pageview, so a naive install would have posted
//    every live guest token to Google and left them sitting in the Pages report,
//    readable by anyone with access to the property. That is a credential leak
//    with a UI on top of it.
//
//    Three separate things stop that here, and each is enough on its own:
//      1. init() refuses to run at all on a /g/ path — the guest page never
//         loads gtag, so there is nothing to leak from.
//      2. every path is passed through cleanPath(), which replaces a token
//         segment with a literal and drops the query string and hash entirely.
//      3. page_location is built from cleanPath() rather than being left to GA,
//         so even the automatic field carries the redacted value.
//    Two of them are redundant. That is the point: a leak this expensive should
//    need three mistakes, not one.
//
// 🟥 NOTHING IS LOADED BEFORE CONSENT. Not gtag.js, not a DNS lookup, not a
//    cookie. Consent Mode's "load the tag, withhold the cookies" pattern is the
//    common implementation and it still contacts Google on every visit — which
//    is not what a person pressing Decline believes they asked for. Here the
//    script tag is only ever created inside grant(), so Decline means Google is
//    never told the visit happened.

const KEY = 'allo_cookie_consent_v1';
const GRANTED = 'granted';
const DENIED = 'denied';

/* The measurement id comes from the build environment, never from source — a
   different property for staging is then a build flag rather than a code edit,
   and an unset value simply turns the whole thing off. */
const GA_ID = (process.env.REACT_APP_GA_ID || '').trim();

let started = false;
const listeners = new Set();

/* ══ THE VISITOR'S CHOICE ══════════════════════════════════════════════════ */

/** 'granted' | 'denied' | null (never asked) */
/* ⚖️ GLOBAL PRIVACY CONTROL — a browser that says "do not sell or share" (navigator.globalPrivacyControl)
   is an analytics "no" before anyone asks; the Privacy Policy promises it (allloapp.com/privacy §10). */
export const gpcOn = () => { try { return navigator.globalPrivacyControl === true; } catch { return false; } };

export function consentState() {
  if (gpcOn()) return DENIED;
  try {
    const v = localStorage.getItem(KEY);
    return v === GRANTED || v === DENIED ? v : null;
  } catch {
    /* 🟢 A BROWSER THAT REFUSES STORAGE HAS NOT CONSENTED. Private windows and
       "block all cookies" both throw here, and the honest reading of that is
       "no", not "ask again and assume yes". Returning null would re-show the
       banner on every page load, which is its own kind of dark pattern. */
    return DENIED;
  }
}

function remember(value) {
  try { localStorage.setItem(KEY, value); } catch { /* nothing we can do */ }
  listeners.forEach((fn) => { try { fn(value); } catch { /* one bad listener */ } });
}

/** Subscribe to changes — the banner and the settings link both need this. */
export function onConsentChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/* ══ THE PATH, WITH THE SECRET TAKEN OUT ═══════════════════════════════════ */

/**
 * 🟥 THE TOKEN PATTERN IS COPIED FROM guestTokenFromLocation(), DELIBERATELY
 *    LOOSER. That function requires 8+ characters of a specific alphabet
 *    because it has to decide whether a route IS a guest link. This one only
 *    has to decide whether a segment MIGHT be a secret, so it matches anything
 *    after /g/ — a shorter token, a future format, a typo. Being too eager here
 *    costs an unreadable analytics row; being too strict costs a leaked
 *    conversation.
 */
export function cleanPath(href) {
  let path = '/';
  /* 🟢 ANYTHING THAT IS NOT A STRING IS NOT A PATH. `new URL(undefined, base)`
     happily returns "/undefined" and an object becomes "/[object Object]" —
     neither throws, so both would have quietly become rows in the report. */
  if (typeof href !== 'string' || !href) return '/';
  try {
    const u = new URL(href, 'https://allloapp.com');
    path = u.pathname || '/';
  } catch {
    path = typeof href === 'string' ? href.split(/[?#]/)[0] : '/';
  }
  /* 🟥 THE QUERY AND HASH GO, WHOLESALE. A token can arrive as ?t=… or #…
     during a redirect, and enumerating the parameter names worth stripping is a
     game you lose the first time somebody adds one. Nothing in this app needs a
     query string measured, so none is sent. */
  path = String(path).split(/[?#]/)[0];
  // any /g/<anything> becomes one row in the report, with no secret in it
  path = path.replace(/^\/g\/[^/]+.*$/i, '/g/[link]');
  return path || '/';
}

/** Is this page one we measure at all? */
export function isMeasurablePath(href) {
  return !/^\/g\//i.test(cleanPath(href));
}

/* ══ LOADING GTAG ══════════════════════════════════════════════════════════ */

function loadGtag() {
  if (started || !GA_ID) return;
  started = true;

  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line func-names
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;

  /* 🟢 CONSENT MODE IS STILL DECLARED, even though nothing loads before Accept.
     It costs two lines and it makes the state explicit to Google rather than
     implied by our loading order — and it is what a later "withdraw consent"
     needs in order to mean anything. Advertising stays denied in every case:
     this property measures visits, it does not build audiences. */
  gtag('consent', 'default', {
    ad_storage: DENIED,
    ad_user_data: DENIED,
    ad_personalization: DENIED,
    analytics_storage: DENIED,
  });
  gtag('consent', 'update', { analytics_storage: GRANTED });

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`;
  document.head.appendChild(s);

  gtag('js', new Date());
  /* 🟥 send_page_view: false IS LOAD-BEARING, NOT A PREFERENCE. Left on, GA
     sends a pageview the instant it loads, using the real URL — before any of
     the redaction below gets a chance to run. On a single-page app it would
     also miss every route change after the first. Every pageview here is sent
     by hand, from a path that has already been cleaned. */
  gtag('config', GA_ID, {
    send_page_view: false,
    anonymize_ip: true,
  });
}

/* ══ THE PUBLIC API ════════════════════════════════════════════════════════ */

/**
 * Start measuring, if the visitor has said yes and this page may be measured.
 * Safe to call repeatedly — it is what every route change calls.
 */
export function init() {
  if (!GA_ID) return false;
  if (consentState() !== GRANTED) return false;
  if (!isMeasurablePath(window.location.pathname)) return false;
  loadGtag();
  return true;
}

/**
 * Record a pageview.
 *
 * @param title     what to call it in the report
 * @param virtual   an app section like '/app/vibes'. The web client is one HTML
 *                  page with a `view` state rather than a router, so without
 *                  this every screen would be reported as '/' and the whole
 *                  property would say "one page, lots of visits". It still goes
 *                  through cleanPath, so a caller cannot smuggle anything in.
 */
export function pageview(title, virtual) {
  if (!init()) return;
  const path = cleanPath(virtual || window.location.pathname);
  try {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title || document.title || 'Allo',
      /* built from the cleaned path — never from window.location.href, which
         still has the token in it */
      page_location: `${window.location.origin}${path}`,
    });
  } catch { /* analytics must never break a page */ }
}

/** The person pressed Accept. */
export function grant() {
  remember(GRANTED);
  if (window.gtag) {
    try { window.gtag('consent', 'update', { analytics_storage: GRANTED }); } catch {}
  }
  pageview();
}

/**
 * The person pressed Decline, or withdrew consent later.
 *
 * 🟢 A WITHDRAWAL AFTER THE FACT HAS TO DO MORE THAN REMEMBER A FLAG. gtag is
 *    already in the page and cannot be unloaded, so consent is updated to denied
 *    — which stops it using cookies — and the cookies it has already set are
 *    deleted here. Without that last step, "I changed my mind" would leave the
 *    identifier that ties every future visit to this one sitting in the browser.
 */
export function deny() {
  remember(DENIED);
  if (window.gtag) {
    try {
      window.gtag('consent', 'update', {
        analytics_storage: DENIED, ad_storage: DENIED,
        ad_user_data: DENIED, ad_personalization: DENIED,
      });
    } catch {}
  }
  clearGaCookies();
}

/** _ga and _ga_<container>, on this host and on the registrable domain. */
export function clearGaCookies() {
  try {
    const host = window.location.hostname;
    // "app.example.com" also needs ".example.com" — GA sets the broader one
    const parts = host.split('.');
    const domains = [host, `.${host}`];
    if (parts.length > 2) domains.push(`.${parts.slice(-2).join('.')}`);
    const names = document.cookie.split(';')
      .map((c) => c.split('=')[0].trim())
      .filter((n) => n === '_ga' || n.startsWith('_ga_') || n.startsWith('_gid'));
    names.forEach((n) => {
      domains.forEach((d) => {
        document.cookie = `${n}=; Max-Age=0; path=/; domain=${d}`;
      });
      document.cookie = `${n}=; Max-Age=0; path=/`;
    });
  } catch { /* a browser that will not let us clear them will not have set them */ }
}

/** Was analytics configured for this build at all? The banner asks. */
export const analyticsAvailable = () => !!GA_ID;
